/**
 * v3.21-1769 · Dedicated suspension screen.
 *
 * Displayed when the Hub authentication layer rejects a request with
 * the stable envelope `{"code": "ACCOUNT_SUSPENDED", ...}` — either
 * on POST /api/auth/login (valid credentials + suspended user) OR on
 * any authenticated call whose JWT resolves to a now-suspended user
 * (backend enforces via `core.dependencies.get_current_user_from_token`).
 *
 * Contains the exact user-facing message required by v3.21-1769.  No
 * workspace / sidebar is rendered — this is a public route mounted
 * OUTSIDE any authenticated wrapper.
 */
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { ShieldAlert } from 'lucide-react';

export default function SuspendedPage() {
  const navigate = useNavigate();

  const handleReturnToLogin = () => {
    // v3.21-1769 · Clear any local session so the user is not caught
    // in a redirect loop.  Cookie-based refresh tokens are cleared
    // server-side on the next /auth/session/login attempt.
    try {
      sessionStorage.removeItem('access_token');
      localStorage.removeItem('access_token');
    } catch (_e) { /* noop */ }
    navigate('/login', { replace: true });
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-slate-50 px-4"
      data-testid="suspended-page"
    >
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-14 h-14 rounded-full bg-amber-100 flex items-center justify-center">
            <ShieldAlert className="w-7 h-7 text-amber-600" />
          </div>
        </div>
        <h1
          className="text-xl font-semibold text-slate-800 mb-2"
          data-testid="suspended-heading"
        >
          Prestigious Hub
        </h1>
        <p
          className="text-base text-slate-700 mb-2"
          data-testid="suspended-message"
        >
          Your credentials are temporarily suspended.
        </p>
        <p
          className="text-sm text-slate-500 mb-6"
          data-testid="suspended-secondary"
        >
          Please contact your administrator for assistance.
        </p>
        <Button
          onClick={handleReturnToLogin}
          className="w-full"
          data-testid="suspended-return-to-login"
        >
          Return to Login
        </Button>
      </div>
    </div>
  );
}
